<script>
import BotoesNavegacao from '@/components/BotoesNavegacao.vue'
import { formatCpfCnpjDisplay } from '@/utils/cpfCnpj'
import { CONCESSIONARIAS } from '@/data/concessionarias'

export default {
  components: { BotoesNavegacao },

  props: {
    form:        { type: Object,  required: true },
    pdfFile:     { type: File,    default: null },
    docFront:    { type: File,    default: null },
    docBack:     { type: File,    default: null },
    enviando:    { type: Boolean, default: false },
  },

  computed: {
    docFormatado() { return formatCpfCnpjDisplay(this.form.person_number) },
    nomeConcessionaria() {
      const c = CONCESSIONARIAS.find(x => String(x.id) === String(this.form.concessionaria_id))
      return c ? c.nome : '—'
    },
    enderecoCompleto() {
      const f = this.form
      const linha1 = [f.logradouro, f.numero].filter(Boolean).join(', ')
      const compl  = f.complemento ? ` - ${f.complemento}` : ''
      const linha2 = [f.bairro, f.cidade, f.estado].filter(Boolean).join(' / ')
      const cep    = f.cep ? `CEP ${f.cep}` : ''
      return [`${linha1}${compl}`, linha2, cep].filter(Boolean).join(' • ')
    },
  },
}
</script>

<template>
  <div>
    <p class="section-subtitle">
      Revise os dados antes de enviar. Após o envio, nossa equipe entra em contato para concluir o processo.
    </p>

    <div class="review-block">
      <h3 class="review-block__title">Dados pessoais</h3>
      <dl class="review-list">
        <div><dt>Tipo</dt><dd>{{ form.person_type === 'pj' ? 'Pessoa jurídica' : 'Pessoa física' }}</dd></div>
        <div><dt>{{ form.person_type === 'pj' ? 'Razão social' : 'Nome' }}</dt><dd>{{ form.nome || '—' }}</dd></div>
        <div><dt>{{ form.person_type === 'pj' ? 'CNPJ' : 'CPF' }}</dt><dd>{{ docFormatado || '—' }}</dd></div>
        <div><dt>E-mail</dt><dd>{{ form.email || '—' }}</dd></div>
        <div><dt>Celular</dt><dd>{{ form.telefone || '—' }}</dd></div>
        <template v-if="form.person_type === 'pf'">
          <div><dt>Nacionalidade</dt><dd>{{ form.nacionalidade || '—' }}</dd></div>
          <div><dt>Estado civil</dt><dd>{{ form.estado_civil || '—' }}</dd></div>
        </template>
      </dl>
    </div>

    <div class="review-block">
      <h3 class="review-block__title">Instalação</h3>
      <dl class="review-list">
        <div><dt>Concessionária</dt><dd>{{ nomeConcessionaria }}</dd></div>
        <div><dt>Nº instalação</dt><dd>{{ form.numero_instalacao || '—' }}</dd></div>
        <div><dt>Consumo médio</dt><dd>{{ form.kwh_avg ? form.kwh_avg + ' kWh' : '—' }}</dd></div>
        <div v-if="form.tipo_fase"><dt>Tipo de fase</dt><dd>{{ form.tipo_fase }}</dd></div>
        <div v-if="form.mes_referencia"><dt>Mês de referência</dt><dd>{{ form.mes_referencia }}</dd></div>
        <div class="review-list__full"><dt>Endereço</dt><dd>{{ enderecoCompleto || '—' }}</dd></div>
      </dl>
    </div>

    <div class="review-block">
      <h3 class="review-block__title">Documento de identidade</h3>
      <dl class="review-list">
        <div><dt>Tipo</dt><dd>{{ form.tipo_documento === 'rg' ? 'RG' : 'CNH' }}</dd></div>
        <div>
          <dt>Frente</dt>
          <dd v-if="docFront">📄 {{ docFront.name }}</dd>
          <dd v-else class="review-block__file--missing">Não anexada</dd>
        </div>
        <div>
          <dt>Verso</dt>
          <dd v-if="docBack">📄 {{ docBack.name }}</dd>
          <dd v-else class="review-block__file--missing">{{ form.tipo_documento === 'rg' ? 'Obrigatório' : 'Opcional' }}</dd>
        </div>
      </dl>
    </div>

    <div class="review-block">
      <h3 class="review-block__title">Conta de energia</h3>
      <p class="review-block__file">
        <span v-if="pdfFile">📄 {{ pdfFile.name }}</span>
        <span v-else class="review-block__file--missing">Nenhum arquivo anexado — você poderá enviar depois.</span>
      </p>
    </div>

    <BotoesNavegacao
      :carregando="enviando"
      rotulo-avancar="Enviar cadastro"
      @voltar="$emit('voltar')"
      @avancar="$emit('enviar')"
    />
  </div>
</template>

<style scoped lang="scss">
@import "~@/design/tokens";

.review-block {
  border: 1px solid $border-subtle;
  border-radius: $radius-input;
  padding: $space-4;
  margin-bottom: $space-4;
}

.review-block__title {
  font-size: 1rem;
  font-weight: 700;
  color: $text-strong;
  margin: 0 0 $space-3;
}

.review-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-3;
  margin: 0;
}

@media (min-width: $bp-md) {
  .review-list { grid-template-columns: 1fr 1fr; }
  .review-list__full { grid-column: 1 / -1; }
}

.review-list > div { display: flex; flex-direction: column; gap: 2px; }
.review-list dt {
  font-size: .75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: $text-muted;
  letter-spacing: .04em;
  margin: 0;
}
.review-list dd {
  font-size: .95rem;
  color: $text-strong;
  margin: 0;
  word-break: break-word;
}

.review-block__file {
  font-size: .95rem;
  color: $text-strong;
  margin: 0;
}

.review-block__file--missing {
  color: $text-muted;
  font-style: italic;
}
</style>
